import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

import { useAuthStore } from '../store/authStore'; // Corrected path: store instead of stores
import { apiClient } from '../services/api'; // Corrected path: services/api instead of lib/apiClient
import { useAuth0 } from '@auth0/auth0-react'; // Add this import

interface SyncResponse {
  status: string;
  task_id: string | null;
}

const TradeSyncButton: React.FC = () => {
  const { isApiAuthReady, setSyncTaskId, setSyncTaskStatus } = useAuthStore(state => ({
    isApiAuthReady: state.isApiAuthReady,
    setSyncTaskId: state.setSyncTaskId,
    setSyncTaskStatus: state.setSyncTaskStatus, // Get setter from store
  }));
  const { getAccessTokenSilently } = useAuth0(); // Get it from useAuth0

  const syncMutation = useMutation<SyncResponse, Error>({
    mutationFn: async () => {
      // Now getAccessTokenSilently comes from useAuth0
      const token = await getAccessTokenSilently();
      const response = await apiClient.post('/trades/sync', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },    onSuccess: (data) => {
      // Dismiss the loading toast
      toast.dismiss('trade-sync-init');
      
      if (data.task_id) {
        toast.success('Trade sync started in background.');
        setSyncTaskId(data.task_id, 'trades'); // Pass task type
        // Reset/Set the global status to pending when a new task starts
        setSyncTaskStatus({
            status: 'pending',
            message: 'Sync initiated, connecting...',
            progress: 0,
            details: [`${new Date().toISOString()}: Sync initiated.`]
        });
      } else if (data.status === 'no accounts configured') {
        toast('No accounts configured for sync.');
        setSyncTaskId(null); // Clear task ID if none started
        setSyncTaskStatus(null); // Clear status
      } else {
         toast.success('Sync request accepted, but no task ID returned.'); // Or handle as needed
         setSyncTaskId(null); // Clear task ID
         setSyncTaskStatus(null); // Clear status
      }
    },    onError: (error) => {
      // Dismiss the loading toast
      toast.dismiss('trade-sync-init');
      
      console.error('Sync initiation failed:', error);
      toast.error(`Sync failed: ${error.message || 'Unknown error'}`);
      // Optionally clear or set error status globally, though SyncStatusIndicator might handle this better
      // setSyncTaskId(null);
      // setSyncTaskStatus({ status: 'error', message: 'Sync initiation failed.', progress: 0 });
    },
  });
  const handleSyncClick = () => {
    if (!isApiAuthReady) {
      toast.error('Authentication not ready. Please wait.');
      return;
    }
    
    // Show immediate feedback toast
    toast.loading('Initiating trade sync...', { id: 'trade-sync-init' });
    
    syncMutation.mutate();
  };
  return (
    <motion.button
      onClick={handleSyncClick}
      disabled={syncMutation.isPending || !isApiAuthReady}
      className="relative overflow-hidden px-4 py-2 text-sm rounded-lg font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
      whileHover={{ scale: 1.05, y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      {syncMutation.isPending ? (
        <>
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Syncing...
        </>
      ) : (
        'Sync Recent Trades'
      )}
    </motion.button>
  );
};

export default TradeSyncButton;